import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  HammerIcon,
  type LucideIcon,
} from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import {
  KitThreadCheckController,
  KitThreadCheckPhase,
  isFailedState,
} from 'hooks/useKitThreadCheck';
import { KitThreadCheckKind } from 'lib/api/kitThreadCheck';

import {
  KitPosthogEvents,
  getKitThreadCheckRequestedLogProperties,
} from '../utils/posthogEvents';

export const KIT_THREAD_CHECK_CHIP_LABEL: Record<KitThreadCheckKind, string> = {
  [KitThreadCheckKind.QuoteRequest]: 'Check for Quotes',
  [KitThreadCheckKind.LoadBuilding]: 'Check for Loads',
};

const KIT_THREAD_CHECK_CHIP_ICON: Record<KitThreadCheckKind, LucideIcon> = {
  [KitThreadCheckKind.QuoteRequest]: CircleDollarSign,
  [KitThreadCheckKind.LoadBuilding]: HammerIcon,
};
const CHEVRON_SURFACE = 'hsl(var(--outlook-bg, var(--sidepanel-bg)))';
const LEFT_CHEVRON_BACKGROUND = `linear-gradient(to right, ${CHEVRON_SURFACE} 0%, ${CHEVRON_SURFACE} 75%, transparent 100%)`;
const RIGHT_CHEVRON_BACKGROUND = `linear-gradient(to left, ${CHEVRON_SURFACE} 0%, ${CHEVRON_SURFACE} 75%, transparent 100%)`;

type AskKitThreadCheckProps = {
  // One per kind enabled for the service; nothing renders when empty.
  checks: KitThreadCheckController[];
};

// Lets the rep ask Kit to re-read the thread when it missed a quote request or load.
export function AskKitThreadCheck({ checks }: AskKitThreadCheckProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const nextScrollState = {
      canScrollLeft: container.scrollLeft > 1,
      canScrollRight: maxScrollLeft - container.scrollLeft > 1,
    };
    setScrollState((current) =>
      current.canScrollLeft === nextScrollState.canScrollLeft &&
      current.canScrollRight === nextScrollState.canScrollRight
        ? current
        : nextScrollState
    );
  }, []);

  useEffect(() => {
    updateScrollState();

    const container = scrollRef.current;
    const content = contentRef.current;
    if (!container) {
      return;
    }

    container.addEventListener('scroll', updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);
    if (content) {
      resizeObserver.observe(content);
    }

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    updateScrollState();
  }, [checks, updateScrollState]);

  if (checks.length === 0) {
    return null;
  }

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    container.scrollTo({
      left: direction === 'right' ? container.scrollWidth : 0,
      behavior: 'smooth',
    });
  };

  return (
    <Flex
      align='center'
      className='relative w-full mt-1 mb-2'
      data-testid='ask-kit-thread-check'
    >
      {scrollState.canScrollLeft && (
        <button
          type='button'
          aria-label='Scroll thread checks left'
          onClick={() => scroll('left')}
          className='absolute left-0 top-1/2 z-10 -translate-y-1/2 cursor-pointer p-1 pl-0 text-neutral-500'
          style={{ background: LEFT_CHEVRON_BACKGROUND }}
        >
          <ChevronLeft className='h-4 w-4' aria-hidden />
        </button>
      )}
      <div
        ref={scrollRef}
        className='w-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      >
        <Flex ref={contentRef} className='w-full' gap='xs'>
          {checks.map((check) => {
            const Icon = KIT_THREAD_CHECK_CHIP_ICON[check.kind];

            return (
              <Button
                key={check.kind}
                type='button'
                buttonNamePosthog={KitPosthogEvents.ThreadCheckRequested}
                logProperties={requestedLogProperties(check)}
                variant='unstyled'
                size='auto'
                onClick={check.apply}
                disabled={isChipDisabled(check)}
                className='inline-flex h-6 shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg border border-accent bg-accent-50 px-1.5 text-[11px] font-medium text-accent-700 transition-colors hover:border-accent-400 hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-50'
              >
                <Icon className='h-3 w-3 stroke-accent-600' aria-hidden />
                {KIT_THREAD_CHECK_CHIP_LABEL[check.kind]}
              </Button>
            );
          })}
        </Flex>
      </div>
      {scrollState.canScrollRight && (
        <button
          type='button'
          aria-label='Scroll thread checks right'
          onClick={() => scroll('right')}
          className='absolute right-0 top-1/2 z-10 -translate-y-1/2 cursor-pointer p-1 pr-0 text-neutral-500'
          style={{ background: RIGHT_CHEVRON_BACKGROUND }}
        >
          <ChevronRight className='h-4 w-4' aria-hidden />
        </button>
      )}
    </Flex>
  );
}

// Disabled until we know whether a check is already running, and while one is.
function isChipDisabled(check: KitThreadCheckController): boolean {
  return (
    check.isRunning ||
    check.state.phase === KitThreadCheckPhase.Discovering ||
    check.state.phase === KitThreadCheckPhase.Disabled
  );
}

function requestedLogProperties(
  check: KitThreadCheckController
): Record<string, unknown> {
  return getKitThreadCheckRequestedLogProperties({
    kind: check.kind,
    threadId: check.threadId,
    emailId: check.emailId,
    isRetry: isFailedState(check.state),
  });
}
